// ============================================================================
// <FacilityScopeControl> — the visible widen/narrow control for useFacilityScope.
//
// CW-A (2026-08-04). Rendered identically on the Accounts list/map and on
// /map, so "which facilities am I looking at" is answered the same way and
// LOOKS the same way on both surfaces.
//
// Renders nothing when the user has no assigned facilities (`canScopeToMine`
// false) — there is no meaningful choice to offer, and the effective scope is
// already 'all'.
// ============================================================================

import type { FacilityScope } from './use-facility-scope';

const CHIP_BASE =
  'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-medium transition-colors';
const CHIP_ON = 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink';
const CHIP_OFF =
  'border-tcs-border-strong bg-tcs-bg text-tcs-text-secondary hover:bg-tcs-bg-muted';

export function FacilityScopeControl({
  scope,
  onChange,
  canScopeToMine,
  mineCount,
  allCount,
}: {
  scope: FacilityScope;
  onChange: (next: FacilityScope) => void;
  canScopeToMine: boolean;
  /** Size of the assigned set (AM-of-record ∪ active route assignments). */
  mineCount: number;
  /** Size of the org-wide set. `null` while still loading. */
  allCount: number | null;
}) {
  if (!canScopeToMine) return null;

  const allLabel = allCount === null ? 'All facilities' : `All facilities (${allCount})`;

  return (
    <div
      className="inline-flex items-center gap-1"
      role="group"
      aria-label="Facility scope"
    >
      <button
        type="button"
        aria-pressed={scope === 'mine'}
        onClick={() => onChange('mine')}
        className={`${CHIP_BASE} ${scope === 'mine' ? CHIP_ON : CHIP_OFF}`}
        title="Facilities you are AM-of-record on, or that are on your active route"
      >
        My facilities ({mineCount})
      </button>
      <button
        type="button"
        aria-pressed={scope === 'all'}
        onClick={() => onChange('all')}
        className={`${CHIP_BASE} ${scope === 'all' ? CHIP_ON : CHIP_OFF}`}
        title="Every facility in the organisation"
      >
        {allLabel}
      </button>
    </div>
  );
}
